import { useVerificationStatusQuery } from '@/src/queries/earnings/verification-status'
import Redeem from './redeem'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDisclosure } from '@chakra-ui/react'
import { useUser } from '@/src/store/user-store'
import VerifyPhoneEmail from './verifyphoneemail'
import VerifyID from './verifyid'
import VerifyIDError from './verifyiderror'
import StripeIndividual from '../stripe/individual'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

export default function RedeemAndVerificationFlow({
  launchFlow,
  launchFlowType,
  setLaunchFlow,
}: {
  launchFlow: boolean
  launchFlowType: 'redeem' | 'verify'
  setLaunchFlow: (value: boolean) => void
}) {
  const verificationStatus = useVerificationStatusQuery()
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { user, _hasHydrated } = useUser()
  const isBusinessUser = String(user?.isBusinessUser) === '1'
  const isFlowLaunched = useRef(false)
  const isRedeemOpened = useRef(false)
  const handledDigiKycStatus = useRef<string>()
  const [errorFlowType, setErrorFlowType] = useState<
    'attempts-over' | 'bank' | 'pan'
  >()
  const [currentStep, setCurrentStep] = useState(0)

  const {
    isOpen: isOpenRedeem,
    onOpen: onOpenRedeem,
    onClose: onCloseRedeem,
  } = useDisclosure()
  const openRedeemOnce = useCallback(() => {
    if (isRedeemOpened.current) return

    isRedeemOpened.current = true
    onOpenRedeem()
  }, [onOpenRedeem])
  const {
    isOpen: isMobileOtpVerifyOpen,
    onOpen: onOpenMobileOtpVerify,
    onClose: onCloseMobileOtpVerify,
  } = useDisclosure()

  const {
    isOpen: isVerifyIDOpen,
    onOpen: onOpenVerifyID,
    onClose: onCloseVerifyID,
  } = useDisclosure()

  const {
    isOpen: isErrorOccurredOpen,
    onOpen: onOpenErrorOccurred,
    onClose: onCloseErrorOccurred,
  } = useDisclosure()
  const idVerificationStatus = isBusinessUser
    ? (verificationStatus.data?.gstVerification ?? 'pending')
    : (verificationStatus.data?.aadhaarVerification ?? 'pending')
  const digiStatus = searchParams.get('digi_status')
  const digiSessionId = searchParams.get('session_id')?.trim()

  useEffect(() => {
    if (
      digiStatus !== 'ok' ||
      !digiSessionId ||
      verificationStatus.isFetching
    ) {
      return
    }

    const kycStatus = verificationStatus.data?.data?.status

    if (!kycStatus || handledDigiKycStatus.current === kycStatus) return

    handledDigiKycStatus.current = kycStatus

    if (kycStatus === 'processing' || kycStatus === 'failed') {
      onOpenVerifyID()
    } else if (kycStatus === 'success') {
      onCloseVerifyID()
      openRedeemOnce()
    }

    router.replace(pathname, { scroll: false })
  }, [
    digiSessionId,
    digiStatus,
    onCloseVerifyID,
    openRedeemOnce,
    onOpenVerifyID,
    pathname,
    router,
    verificationStatus.data?.data?.status,
    verificationStatus.isFetching,
  ])

  useEffect(() => {
    if (launchFlow && !isFlowLaunched.current) {
      if (_hasHydrated && user?.mobileotpverify != '1') {
        isFlowLaunched.current = true
        onOpenMobileOtpVerify()
      } else if (
        verificationStatus.data?.attemptLeft != '0' &&
        (verificationStatus.data?.bankVerification === 'failed' ||
          verificationStatus.data?.panVerification === 'failed' ||
          idVerificationStatus === 'failed')
      ) {
        isFlowLaunched.current = true
        onOpenErrorOccurred()
        setErrorFlowType(
          verificationStatus.data?.bankVerification === 'failed'
            ? 'bank'
            : 'pan'
        )
      } else if (
        verificationStatus.data?.attemptLeft != '0' &&
        (verificationStatus.data?.bankVerification === 'pending' ||
          verificationStatus.data?.panVerification === 'pending' ||
          idVerificationStatus === 'pending')
      ) {
        isFlowLaunched.current = true
        onOpenVerifyID()
      } else if (
        verificationStatus.data?.bankVerification === 'verified' &&
        verificationStatus.data?.panVerification === 'verified' &&
        idVerificationStatus === 'verified' &&
        launchFlowType === 'redeem'
      ) {
        isFlowLaunched.current = true
        openRedeemOnce()
      } else if (
        verificationStatus.data?.attemptLeft === '0' &&
        ((verificationStatus.data?.bankVerification === 'failed' ||
          verificationStatus.data?.bankVerification === 'pending') ||
          (verificationStatus.data?.panVerification === 'failed' ||
            verificationStatus.data?.panVerification === 'pending') ||
          (idVerificationStatus === 'failed' ||
            idVerificationStatus === 'pending'))
      ) {
        isFlowLaunched.current = true
        onOpenErrorOccurred()
        setErrorFlowType('attempts-over')
      }
    }
  }, [
    launchFlow,
    launchFlowType,
    _hasHydrated,
    user,
    onOpenMobileOtpVerify,
    onOpenVerifyID,
    verificationStatus.data,
    onOpenErrorOccurred,
    openRedeemOnce,
    idVerificationStatus,
  ])

  const handleOTPVerifiedClose = () => {
    onCloseMobileOtpVerify()
    if (
      user?.countryShortname === 'US' ||
      user?.countryShortname === 'CA' ||
      user?.countryShortname === 'GB' ||
      user?.countryShortname === 'AE'
    ) {
      onStripeOpen()
    }else{
      onOpenVerifyID()
    }
  }

  const handleMaxAttemptsFlow = () => {
    onCloseVerifyID()
    onOpenErrorOccurred()
    setErrorFlowType('attempts-over')
  }

  const handleAccountVerified = () => {
    if (launchFlowType === 'redeem') {
      openRedeemOnce()
    }
  }

  const handleProceedFromErrorFlow = () => {
    onCloseErrorOccurred()
    onOpenVerifyID()
  }

  const {
    isOpen: isStripeOpen,
    onOpen: onStripeOpen,
    onClose: onStripeClose,
  } = useDisclosure()
  useEffect(() => {
    if (currentStep === 3) {
      onStripeClose()
      openRedeemOnce()
    }
  }, [currentStep, openRedeemOnce, onStripeClose])
  return (
    <>
      <Redeem
        isOpen={isOpenRedeem}
        onClose={() => {
          onCloseRedeem()
          isRedeemOpened.current = false
          setLaunchFlow(false)
          isFlowLaunched.current = false
        }}
        user={user}
      />
      <VerifyPhoneEmail
        isOpen={isMobileOtpVerifyOpen}
        onClose={() => {
          onCloseMobileOtpVerify()
          setLaunchFlow(false)
          isFlowLaunched.current = false
        }}
        onVerifyClose={handleOTPVerifiedClose}
      />
      {isVerifyIDOpen && (
        <VerifyID
          isOpen={isVerifyIDOpen}
          onClose={() => {
            onCloseVerifyID()
            setLaunchFlow(false)
            isFlowLaunched.current = false
          }}
          onVerifiedClose={handleAccountVerified}
          handleMaxAttemptsFlow={handleMaxAttemptsFlow}
        />
      )}
      <VerifyIDError
        isOpen={isErrorOccurredOpen}
        onClose={() => {
          onCloseErrorOccurred()
          setLaunchFlow(false)
          isFlowLaunched.current = false
        }}
        variant={errorFlowType}
        handleProceed={handleProceedFromErrorFlow}
      />
      <StripeIndividual
        onClose={() => {
          onStripeClose()
          setCurrentStep(0)
        }}
        isOpen={isStripeOpen}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />
    </>
  )
}
